import { z } from 'zod'

export const AnalyticsEventEnum = {
  AuthenticatedPageViewed: 'authenticated_page_viewed',
  ToolSessionStarted: 'tool_session_started',
  ToolSessionEnded: 'tool_session_ended',
  FlowCompleted: 'flow_completed',
  OnboardingStepViewed: 'onboarding_step_viewed',
  OnboardingCompleted: 'onboarding_completed',
  ScheduleConsultationClicked: 'schedule_consultation_clicked',
  BannerCtaClicked: 'banner_cta_clicked',
  UserRegistered: 'user_registered',
  EmailConfirmationAwaiting: 'email_confirmation_awaiting',
  EmailConfirmed: 'email_confirmed',
  MagicLinkLogin: 'magic_link_login',
  SsoLogin: 'sso_login',
  TeammateOnboardingCompleted: 'teammate_onboarding_completed',
} as const

export const AnalyticsEventEnumValues = [
  'authenticated_page_viewed',
  'tool_session_started',
  'tool_session_ended',
  'flow_completed',
  'onboarding_step_viewed',
  'onboarding_completed',
  'schedule_consultation_clicked',
  'banner_cta_clicked',
  'user_registered',
  'email_confirmation_awaiting',
  'email_confirmed',
  'magic_link_login',
  'sso_login',
  'teammate_onboarding_completed',
] as const

export type AnalyticsEventEnumType = (typeof AnalyticsEventEnum)[keyof typeof AnalyticsEventEnum]

export const AnalyticsEventSchema = z.enum(AnalyticsEventEnumValues)

/** Core tools for analytics and UI across portals. */
export const ProductToolIdEnum = {
  BusinessIntelligence: 'business_intelligence',
  MarketMakerMonitoring: 'market_maker_monitoring',
  EngageAMarketMaker: 'engage_a_market_maker',
  TokenDesigner: 'token_designer',
  ExchangeListing: 'exchange_listing',
  MarketMakerLeaderboards: 'market_maker_leaderboards',
  TokenUnlockMonitoring: 'token_unlock_monitoring',
} as const

export const ProductToolIdEnumValues = [
  'business_intelligence',
  'market_maker_monitoring',
  'engage_a_market_maker',
  'token_designer',
  'exchange_listing',
  'market_maker_leaderboards',
  'token_unlock_monitoring',
] as const

export type ProductToolIdEnumType = (typeof ProductToolIdEnum)[keyof typeof ProductToolIdEnum]

export const ProductToolIdSchema = z.enum(ProductToolIdEnumValues)

// Per-tool flow enums — completable actions tracked by `flow_completed` event.

export const TokenDesignerFlowEnum = {
  Save: 'save',
  MarkComplete: 'mark_complete',
  Publish: 'publish',
  Unpublish: 'unpublish',
} as const

export const TokenDesignerFlowEnumValues = ['save', 'mark_complete', 'publish', 'unpublish'] as const

export type TokenDesignerFlowEnumType = (typeof TokenDesignerFlowEnum)[keyof typeof TokenDesignerFlowEnum]

export const TokenDesignerFlowSchema = z.enum(TokenDesignerFlowEnumValues)

export const EngageMarketMakerFlowEnum = {
  SubmitRfq: 'submit_rfq',
  EngageMarketMaker: 'engage_market_maker',
  AcceptQuotes: 'accept_quotes',
  RequestRequote: 'request_requote',
  CancelRfq: 'cancel_rfq',
} as const

export const EngageMarketMakerFlowEnumValues = [
  'submit_rfq',
  'engage_market_maker',
  'accept_quotes',
  'request_requote',
  'cancel_rfq',
] as const

export type EngageMarketMakerFlowEnumType = (typeof EngageMarketMakerFlowEnum)[keyof typeof EngageMarketMakerFlowEnum]

export const EngageMarketMakerFlowSchema = z.enum(EngageMarketMakerFlowEnumValues)

export const BusinessIntelligenceFlowEnum = {
  CreateLog: 'create_log',
  UpdateLog: 'update_log',
  DeleteLog: 'delete_log',
} as const

export const BusinessIntelligenceFlowEnumValues = ['create_log', 'update_log', 'delete_log'] as const

export type BusinessIntelligenceFlowEnumType =
  (typeof BusinessIntelligenceFlowEnum)[keyof typeof BusinessIntelligenceFlowEnum]

export const BusinessIntelligenceFlowSchema = z.enum(BusinessIntelligenceFlowEnumValues)

export type ProductToolFlowType =
  TokenDesignerFlowEnumType | EngageMarketMakerFlowEnumType | BusinessIntelligenceFlowEnumType

// Onboarding analytics enums — used by `onboarding_step_viewed` and `onboarding_completed`.

export const OnboardingStepEnum = {
  ProjectType: 'projectType',
  TokenListed: 'tokenListed',
  ProjectDetails: 'projectDetails',
  OrganizationDetails: 'organizationDetails',
  AreasOfInterest: 'areasOfInterest',
  OwnerDetails: 'ownerDetails',
  OrganizationUserDetails: 'organizationUserDetails',
  MemberInvitations: 'memberInvitations',
} as const

export const OnboardingStepEnumValues = [
  'projectType',
  'tokenListed',
  'projectDetails',
  'organizationDetails',
  'areasOfInterest',
  'ownerDetails',
  'organizationUserDetails',
  'memberInvitations',
] as const

export type OnboardingStepEnumType = (typeof OnboardingStepEnum)[keyof typeof OnboardingStepEnum]

export const OnboardingStepSchema = z.enum(OnboardingStepEnumValues)

export const OnboardingFlowEnum = {
  Project: 'project',
  Researcher: 'researcher',
  Advisor: 'advisor',
} as const

export const OnboardingFlowEnumValues = ['project', 'researcher', 'advisor'] as const

export type OnboardingFlowEnumType = (typeof OnboardingFlowEnum)[keyof typeof OnboardingFlowEnum]

export const OnboardingFlowSchema = z.enum(OnboardingFlowEnumValues)

// Sources for `schedule_consultation_clicked` — identifies the UI surface that triggered the click.

export const ConsultationSourceEnum = {
  NavSidebar: 'nav_sidebar',
  NavSlideover: 'nav_slideover',
  DashboardFooterCta: 'dashboard_footer_cta',
  DashboardAdvisory: 'dashboard_advisory',
  DashboardAdvisoryDrawer: 'dashboard_advisory_drawer',
  DashboardActiveAdvisor: 'dashboard_active_advisor',
  SupportPage: 'support_page',
  EngageMarketMakerDetails: 'engage_market_maker_details',
  RfqSuccess: 'rfq_success',
  AcademyArticle: 'academy_article',
  ReadyToStart: 'ready_to_start',
  GoogleSheetsModal: 'google_sheets_modal',
  PublicFooterCta: 'public_footer_cta',
  MmmOverviewAdvisory: 'mmm_overview_advisory',
  HomepageAdvisory: 'homepage_advisory',
  RfqDemoCta: 'rfq_demo_cta',
  BusinessIntelligenceAdvisory: 'business_intelligence_advisory',
  RfqOverviewAdvisory: 'rfq_overview_advisory',
  RfqUpdateRfq: 'rfq_update_rfq',
  AboutAdvisorsHero: 'about_advisors_hero',
  AboutAdvisoryPhases: 'about_advisory_phases',
  AboutAdvisorySolutions: 'about_advisory_solutions',
  AboutAdvisoryTeam: 'about_advisory_team',
  AboutAdvisoryActivities: 'about_advisory_activities',
  AboutWhatIsForgdMission: 'about_what_is_forgd_mission',
  AboutWhatIsForgdTeam: 'about_what_is_forgd_team',
  AboutContactAdvisory: 'about_contact_advisory',
  AboutPricing: 'about_pricing',
  DesignTokenomicsAdvisory: 'design_tokenomics_advisory',
  ExchangeListingSidebar: 'exchange_listing_sidebar',
  ExchangeListingOverview: 'exchange_listing_overview',
  PublicResearchMobileHeader: 'public_research_mobile_header',
  UerExchangeCta: 'uer_exchange_cta',
  UerPageCta: 'uer_page_cta',
} as const

export const ConsultationSourceEnumValues = [
  'nav_sidebar',
  'nav_slideover',
  'dashboard_footer_cta',
  'dashboard_advisory',
  'dashboard_advisory_drawer',
  'dashboard_active_advisor',
  'support_page',
  'engage_market_maker_details',
  'rfq_success',
  'academy_article',
  'ready_to_start',
  'google_sheets_modal',
  'public_footer_cta',
  'mmm_overview_advisory',
  'homepage_advisory',
  'rfq_demo_cta',
  'business_intelligence_advisory',
  'rfq_overview_advisory',
  'rfq_update_rfq',
  'about_advisors_hero',
  'about_advisory_phases',
  'about_advisory_solutions',
  'about_advisory_team',
  'about_advisory_activities',
  'about_what_is_forgd_mission',
  'about_what_is_forgd_team',
  'about_contact_advisory',
  'about_pricing',
  'design_tokenomics_advisory',
  'exchange_listing_sidebar',
  'exchange_listing_overview',
  'public_research_mobile_header',
  'uer_exchange_cta',
  'uer_page_cta',
] as const

export type ConsultationSourceEnumType = (typeof ConsultationSourceEnum)[keyof typeof ConsultationSourceEnum]

export const ConsultationSourceSchema = z.enum(ConsultationSourceEnumValues)
